/**
 * Stable error taxonomy.
 *
 * Codes are a public contract: the SPA switches on them, Sentry fingerprints on them and
 * `docs/AGENT-RUNBOOK.md` triage keys off them. Renaming a code is a breaking change —
 * add a new one instead.
 */
export const ERROR_CODES = [
  // generic
  'INTERNAL',
  'NOT_FOUND',
  'VALIDATION_FAILED',
  'CONFLICT',
  'VERSION_CONFLICT',
  'RATE_LIMITED',
  'PAYLOAD_TOO_LARGE',
  'DEPENDENCY_UNAVAILABLE',
  'TIMEOUT',

  // auth / access
  'UNAUTHENTICATED',
  'FORBIDDEN',
  'GEO_SCOPE_DENIED',
  'TWO_FACTOR_REQUIRED',
  'INVALID_CREDENTIALS',

  // domain — reference data
  'PAYMENT_METHOD_MISSING_LOGO',
  'KEYWORD_MISSING_GEO_LANG',
  'DICTIONARY_COVERAGE_TOO_LOW',
  'GEO_LANGUAGE_NOT_ALLOWED',

  // domain — build pipeline
  'BUILD_STEP_BLOCKED',
  'ENTITY_FILL_TOO_LOW',
  'TEMPLATE_NOT_ASSIGNED',
  'DOMAIN_NOT_AVAILABLE',
  'NO_PAGES_RESOLVED',
  'BLOCK_LOCKED',
  'FORCE_UNLOCK_DENIED',

  // domain — content / llm
  'LLM_OUTPUT_INVALID',
  'LLM_DAILY_TOKEN_LIMIT',
  'PROMPT_PROFILE_INVALID',
  'CONTENT_CHECK_FAILED',

  // domain — bulk / tech layer
  'BULK_LIMIT_EXCEEDED',
  'BULK_SITE_UNDER_ABUSE',
  'BULK_SNAPSHOT_MISSING',
  'CONFIRMATION_REQUIRED',
  'CONFIRMATION_EXPIRED',
  'ROLLBACK_UNAVAILABLE',

  // domain — integrations
  'INTEGRATION_DISABLED',
  'INTEGRATION_AUTH_FAILED',
  'GSC_QUOTA_EXCEEDED',
  'REGISTRAR_REJECTED',
  'HOSTING_DEPLOY_FAILED',
  'SSRF_BLOCKED',
  'UNSUPPORTED_FILE_TYPE',
  'IMPORT_FILE_TOO_LARGE',
] as const;

export type ErrorCode = (typeof ERROR_CODES)[number];

const HTTP_STATUS: Record<ErrorCode, number> = {
  INTERNAL: 500,
  NOT_FOUND: 404,
  VALIDATION_FAILED: 400,
  CONFLICT: 409,
  VERSION_CONFLICT: 409,
  RATE_LIMITED: 429,
  PAYLOAD_TOO_LARGE: 413,
  DEPENDENCY_UNAVAILABLE: 503,
  TIMEOUT: 504,

  UNAUTHENTICATED: 401,
  FORBIDDEN: 403,
  GEO_SCOPE_DENIED: 403,
  TWO_FACTOR_REQUIRED: 403,
  INVALID_CREDENTIALS: 401,

  PAYMENT_METHOD_MISSING_LOGO: 422,
  KEYWORD_MISSING_GEO_LANG: 422,
  DICTIONARY_COVERAGE_TOO_LOW: 422,
  GEO_LANGUAGE_NOT_ALLOWED: 422,

  BUILD_STEP_BLOCKED: 422,
  ENTITY_FILL_TOO_LOW: 422,
  TEMPLATE_NOT_ASSIGNED: 422,
  DOMAIN_NOT_AVAILABLE: 422,
  NO_PAGES_RESOLVED: 422,
  BLOCK_LOCKED: 409,
  FORCE_UNLOCK_DENIED: 403,

  LLM_OUTPUT_INVALID: 502,
  LLM_DAILY_TOKEN_LIMIT: 429,
  PROMPT_PROFILE_INVALID: 422,
  CONTENT_CHECK_FAILED: 422,

  BULK_LIMIT_EXCEEDED: 422,
  BULK_SITE_UNDER_ABUSE: 422,
  BULK_SNAPSHOT_MISSING: 409,
  CONFIRMATION_REQUIRED: 428,
  CONFIRMATION_EXPIRED: 410,
  ROLLBACK_UNAVAILABLE: 409,

  INTEGRATION_DISABLED: 503,
  INTEGRATION_AUTH_FAILED: 502,
  GSC_QUOTA_EXCEEDED: 429,
  REGISTRAR_REJECTED: 502,
  HOSTING_DEPLOY_FAILED: 502,
  SSRF_BLOCKED: 400,
  UNSUPPORTED_FILE_TYPE: 415,
  IMPORT_FILE_TOO_LARGE: 413,
};

/** Ukrainian operator-facing text. Never contains secrets or internal identifiers. */
const PUBLIC_MESSAGE: Record<ErrorCode, string> = {
  INTERNAL: 'Внутрішня помилка. Спробуйте ще раз або зверніться до адміністратора.',
  NOT_FOUND: 'Запис не знайдено.',
  VALIDATION_FAILED: 'Дані не пройшли валідацію.',
  CONFLICT: 'Конфлікт даних.',
  VERSION_CONFLICT: 'Запис змінено іншим користувачем. Оновіть сторінку.',
  RATE_LIMITED: 'Забагато запитів. Спробуйте пізніше.',
  PAYLOAD_TOO_LARGE: 'Занадто великий обсяг даних.',
  DEPENDENCY_UNAVAILABLE: 'Залежний сервіс недоступний.',
  TIMEOUT: 'Час очікування вичерпано.',

  UNAUTHENTICATED: 'Потрібна авторизація.',
  FORBIDDEN: 'Недостатньо прав.',
  GEO_SCOPE_DENIED: 'Ці ГЕО поза вашою зоною доступу.',
  TWO_FACTOR_REQUIRED: 'Потрібно налаштувати двофакторну автентифікацію.',
  INVALID_CREDENTIALS: 'Невірний email або пароль.',

  PAYMENT_METHOD_MISSING_LOGO: 'Платіжний метод без світлого SVG не може потрапити у збірку.',
  KEYWORD_MISSING_GEO_LANG: 'Кожен ключ мусить мати ГЕО і мову.',
  DICTIONARY_COVERAGE_TOO_LOW: 'Покриття словника нижче порогу.',
  GEO_LANGUAGE_NOT_ALLOWED: 'Мова не дозволена для цього ГЕО-пресету.',

  BUILD_STEP_BLOCKED: 'Крок збірки заблоковано: є невирішені блокери.',
  ENTITY_FILL_TOO_LOW: 'Сутність заповнена нижче порогу.',
  TEMPLATE_NOT_ASSIGNED: 'Шаблон не призначено.',
  DOMAIN_NOT_AVAILABLE: 'Домен недоступний.',
  NO_PAGES_RESOLVED: 'Не вдалося визначити жодної сторінки для збірки.',
  BLOCK_LOCKED: 'Блок захищено ручною правкою.',
  FORCE_UNLOCK_DENIED: 'Примусове зняття замка доступне лише super admin.',

  LLM_OUTPUT_INVALID: 'Відповідь моделі не відповідає очікуваній схемі.',
  LLM_DAILY_TOKEN_LIMIT: 'Денний ліміт токенів вичерпано.',
  PROMPT_PROFILE_INVALID: 'Промпт-профіль некоректний.',
  CONTENT_CHECK_FAILED: 'Контент не пройшов перевірки.',

  BULK_LIMIT_EXCEEDED: 'Перевищено ліміт масової операції.',
  BULK_SITE_UNDER_ABUSE: 'Сайт має відкритий абуз-кейс і виключений з масових операцій.',
  BULK_SNAPSHOT_MISSING: 'Немає снепшота — операція незворотна, виконання заборонено.',
  CONFIRMATION_REQUIRED: 'Операція вимагає підтвердження другої ролі.',
  CONFIRMATION_EXPIRED: 'Термін підтвердження минув.',
  ROLLBACK_UNAVAILABLE: 'Відкат недоступний для цієї операції.',

  INTEGRATION_DISABLED: 'Інтеграція вимкнена.',
  INTEGRATION_AUTH_FAILED: 'Помилка авторизації інтеграції.',
  GSC_QUOTA_EXCEEDED: 'Квоту Search Console вичерпано.',
  REGISTRAR_REJECTED: 'Реєстратор відхилив операцію.',
  HOSTING_DEPLOY_FAILED: 'Деплой на хостинг не вдався.',
  SSRF_BLOCKED: 'URL заблоковано політикою безпеки.',
  UNSUPPORTED_FILE_TYPE: 'Непідтримуваний тип файлу.',
  IMPORT_FILE_TOO_LARGE: 'Файл перевищує ліміт 50 МБ.',
};

export type AppErrorMeta = Readonly<Record<string, unknown>>;

export class AppError extends Error {
  readonly code: ErrorCode;
  readonly httpStatus: number;
  readonly publicMessage: string;
  readonly meta: AppErrorMeta;

  constructor(
    code: ErrorCode,
    options?: {
      /** Internal detail for logs and Sentry. Never reaches the client. */
      message?: string;
      /**
       * Opt-in sentence appended to the canned public message.
       *
       * `message` is deliberately private, which is right for infrastructure failures and
       * wrong for business rules: «Дані не пройшли валідацію.» tells the user nothing they
       * can act on. A service that refuses for a reason the user must know passes it here —
       * and because it is opt-in, nothing leaks by default.
       */
      publicDetail?: string;
      meta?: AppErrorMeta;
      cause?: unknown;
    },
  ) {
    super(options?.message ?? PUBLIC_MESSAGE[code], { cause: options?.cause });
    this.name = 'AppError';
    this.code = code;
    this.httpStatus = HTTP_STATUS[code];
    this.publicMessage =
      options?.publicDetail === undefined
        ? PUBLIC_MESSAGE[code]
        : `${PUBLIC_MESSAGE[code]} ${options.publicDetail}`;
    this.meta = options?.meta ?? {};
  }

  toJSON(): { code: ErrorCode; httpStatus: number; publicMessage: string; meta: AppErrorMeta } {
    return {
      code: this.code,
      httpStatus: this.httpStatus,
      publicMessage: this.publicMessage,
      meta: this.meta,
    };
  }
}

export function isAppError(e: unknown): e is AppError {
  return e instanceof AppError;
}

export function httpStatusFor(code: ErrorCode): number {
  return HTTP_STATUS[code];
}

export function publicMessageFor(code: ErrorCode): string {
  return PUBLIC_MESSAGE[code];
}
